import { Component, computed, inject, signal, ViewChild, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ContactStore } from 'app/core/stores/base.store';
import { Contact } from 'app/core/models';
import { FuseConfirmationService } from 'app/core/services/confirmation/confirmation.service';
import { SelfContactDialogComponent } from './self-contact-dialog/self-contact-dialog.component';
import { InsuranceContactDialogComponent } from './insurance-contact-dialog/insurance-contact-dialog.component';

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSortModule,
    MatToolbarModule,
    MatMenuModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.css'
})
export class ContactsComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<Contact>;
  
  private contactstore = inject(ContactStore);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  #fuseConfirmationService = inject(FuseConfirmationService);
  displayedColumns = ['category', 'first_name', 'last_name', 'organization', 'actions'];
  
  // Category priority order for sorting
  private categoryOrder = ['Self', 'Borrower', 'Insurance', 'Income'];
  
  // Computed property that returns sorted contacts
  dataSource = computed(() => {
    const contacts = this.contactstore.entities();
    return this.sortContactsByCategory(contacts);
  });
  
  loading = computed(() => this.contactstore.loading());
  
  constructor(private router: Router) {
  }
  
  ngOnInit() {
    // Check if we need to show any contact dialogs
    this.route.queryParams.subscribe(params => {
      if (params['requireSelfContact'] === 'true') {
        this.openSelfContactDialog();
      }
      if (params['requireInsuranceContact'] === 'true') {
        this.openInsuranceContactDialog();
      }
    });
  }
  
  // Custom sorting function based on category priority
  private sortContactsByCategory(contacts: Contact[]): Contact[] {
    return [...contacts].sort((a, b) => {
      // Get the index of each category in our priority order
      const indexA = this.getCategoryPriorityIndex(a.category);
      const indexB = this.getCategoryPriorityIndex(b.category);
      
      // Sort by category priority first
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      
      // If categories are the same, sort by name
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }
  
  // Helper function to get the priority index of a category
  private getCategoryPriorityIndex(category: string | null): number {
    if (!category) return this.categoryOrder.length; // Put null categories at the end
    const index = this.categoryOrder.indexOf(category);
    return index === -1 ? this.categoryOrder.length : index; // Unknown categories after known ones
  }
  
  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.contactstore.filterEntities({ filter: { search: filterValue } });
  }

  addContact() {
    this.router.navigate(['/contacts/add']);
  }

  editContact(contact: Contact) {
    this.contactstore.selectEntity(contact);
    this.router.navigate(['/contacts/edit']);
  }

  deleteContact(contact: Contact) {
    const confirmation = this.#fuseConfirmationService.open({
      title: 'Delete Contact',
      message: 'Are you sure you want to delete this contact? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.contactstore.delete(contact);
        this.router.navigate(['/contacts']);
      }
    })
  }
  
  // Open the self contact dialog
  openSelfContactDialog() {
    const dialogRef = this.dialog.open(SelfContactDialogComponent, {
      width: '500px',
      disableClose: true // Prevent closing by clicking outside
    });
  }
  
  // Open the insurance contact dialog
  openInsuranceContactDialog() {
    const dialogRef = this.dialog.open(InsuranceContactDialogComponent, {
      width: '500px',
      disableClose: true // Prevent closing by clicking outside
    });
  }
}
